import { FC, RefObject, useCallback, useEffect } from 'react'

import {
  useTicketIdsMutation,
  useTicketsInfiniteQuery,
} from 'modules/Monitoring/api/analytics/queries'
import { ActionDropdown } from 'modules/Monitoring/components/ActionDropdown/ActionDropdown'
import { ResourcesPanelCopyResourcesButton } from 'modules/Monitoring/components/ResourcesPanel/ResourcesPanelCopyResourcesButton'
import { ResourcesPanelList } from 'modules/Monitoring/components/ResourcesPanel/ResourcesPanelList'
import { ResourcesPanelSection } from 'modules/Monitoring/components/ResourcesPanel/ResourcesPanelSection'
import { useAdditionalFilters } from 'modules/Monitoring/GlobalFilters/AdditionalFilters/AdditionalFiltersContext'
import { useChannel } from 'modules/Monitoring/hooks/useChannel'
import { useQueueIdsFilteredBySpecialisation } from 'modules/Monitoring/hooks/useQueueIdsFilteredBySpecialisation'
import { useTicketResponseData } from 'modules/Monitoring/hooks/useTicketResponseData'
import { useTicketsSorting } from 'modules/Monitoring/hooks/useTicketsSorting'
import { ResourcesSideParams, TicketsRequestParams } from 'modules/Monitoring/types'
import { pluralize } from 'modules/Monitoring/utils/pluralize'

type QueueHealthTicketsProps = {
  resourcesSideParams: ResourcesSideParams
  scrollRef: RefObject<HTMLDivElement | null>
}
export const QueueHealthTickets: FC<QueueHealthTicketsProps> = ({
  resourcesSideParams,
  scrollRef,
}) => {
  const { entityName, channel } = useChannel()
  const { preparedFiltersForRequest } = useAdditionalFilters()
  const { activeSorting, sortingOptions, sortingValue, onSortingSelect, onResetSorting } =
    useTicketsSorting()

  const queueIds = useQueueIdsFilteredBySpecialisation(
    resourcesSideParams?.specialisation?.id,
  )
  const ticketsParams: TicketsRequestParams = {
    ...resourcesSideParams!,
    filter: preparedFiltersForRequest,
    channel,
    queueIds,
    sortBy: activeSorting.sortBy,
    page: {
      sortOrder: activeSorting.sortOrder,
    },
  }

  const {
    data: ticketsResponse,
    fetchNextPage,
    isPending,
    hasNextPage,
  } = useTicketsInfiniteQuery(
    ticketsParams,
    Boolean(resourcesSideParams) && queueIds !== undefined,
  )
  const { mutateAsync: getTicketIds } = useTicketIdsMutation()
  const { tickets, ticketCount } = useTicketResponseData(ticketsResponse)

  useEffect(onResetSorting, [resourcesSideParams.filterType, onResetSorting])

  const keyExtractor = useCallback((index: number) => tickets[index].id, [tickets])

  return (
    <ResourcesPanelSection
      title={pluralize(entityName, ticketCount)}
      action={
        <ResourcesPanelCopyResourcesButton
          getResourceIds={
            resourcesSideParams ? () => getTicketIds(ticketsParams) : undefined
          }
          entityName={entityName}
        />
      }
      side={
        <ActionDropdown
          label="Tickets sorting"
          options={sortingOptions}
          value={sortingValue}
          onSelect={onSortingSelect}
        />
      }
      isPending={isPending}
      isEmpty={!ticketCount}
    >
      <ResourcesPanelList
        scrollRef={scrollRef}
        resources={tickets}
        keyExtractor={keyExtractor}
        entityName={entityName}
        fetchNextPage={fetchNextPage}
        isPending={isPending}
        hasNextPage={hasNextPage}
      />
    </ResourcesPanelSection>
  )
}
